import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import DatePicker from 'react-datepicker';
import Select from 'react-select';
import 'react-datepicker/dist/react-datepicker.css';
import styles from './RootCauseAnalysis.module.css';
import Modal from 'react-modal';
import GoToDashboardButton from '../components/GoToDashboardButton';
import SourceFileSummary from './SourceFileSummary';
import { fetchRootCauseAnalysis, fetchRootCauseFilters } from './rootCauseAnalysis/rootCauseAnalysisApi';
import {
    buildRootCauseChartData,
    getRootCauseMetrics,
    groupByProbability,
    rootCauseChartOptions,
} from './rootCauseAnalysis/rootCauseAnalysisData';
import {
    exportRootCauseCSV,
    exportRootCauseChartAsPNG,
    exportRootCausePDF,
} from './rootCauseAnalysis/rootCauseAnalysisExport';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const RootCauseAnalysis = () => {
    const { t } = useTranslation();
    const [analysisResult, setAnalysisResult] = useState({ candidates: [], summary: {}, context: [] });
    const [stepNumbers, setStepNumbers] = useState([]);
    const [selectedStepNumber, setSelectedStepNumber] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [ateSwVersions, setAteSwVersions] = useState([]);
    const [ateSwVersion, setAteSwVersion] = useState('');
    const [serialNumbers, setSerialNumbers] = useState([]);
    const [selectedSerialNumbers, setSelectedSerialNumbers] = useState([]);
    const [selectedFamilies, setSelectedFamilies] = useState([]);
    const [testspecs, setTestspecs] = useState([]);
    const [selectedTestspec, setSelectedTestspec] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isHelpOpen, setIsHelpOpen] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalDetails, setModalDetails] = useState(null);
    const [showProbabilities, setShowProbabilities] = useState(true);

    const candidates = useMemo(() => analysisResult.candidates || [], [analysisResult.candidates]);
    const summary = useMemo(() => analysisResult.summary || {}, [analysisResult.summary]);
    const context = useMemo(() => analysisResult.context || [], [analysisResult.context]);
    const chartData = useMemo(() => buildRootCauseChartData(candidates.slice(0, 12)), [candidates]);
    const groupedProbabilities = useMemo(() => groupByProbability(candidates), [candidates]);
    const metrics = useMemo(() => getRootCauseMetrics(candidates, summary), [candidates, summary]);
    const hasData = candidates.length > 0;

    useEffect(() => {
        const loadFilters = async () => {
            try {
                const filters = await fetchRootCauseFilters();
                setStepNumbers(filters.stepNumbers);
                setSerialNumbers(filters.serialNumbers);
                setAteSwVersions(filters.ateSwVersions);
                setTestspecs(filters.testspecs || []);
            } catch (fetchError) {
                console.error('Error fetching root cause filters:', fetchError);
                setError(t('Some filter options could not be loaded.'));
            }
        };

        loadFilters();
    }, [t]);

    const fetchAnalysisData = async (overrides = {}) => {
        setLoading(true);
        setError(null);

        try {
            const result = await fetchRootCauseAnalysis({
                selectedStepNumber,
                startDate,
                endDate,
                ateSwVersion,
                selectedSerialNumbers,
                selectedFamilies,
                selectedTestspec,
                ...overrides,
            });
            setAnalysisResult(result);
        } catch (fetchError) {
            console.error('Error fetching analysis data:', fetchError);
            setAnalysisResult({ candidates: [], summary: {}, context: [] });
            setError(t('Failed to load root cause analysis. Please try again.'));
        } finally {
            setLoading(false);
        }
    };

    const resetFilters = () => {
        setSelectedStepNumber('');
        setStartDate(null);
        setEndDate(null);
        setAteSwVersion('');
        setSelectedSerialNumbers([]);
        setSelectedFamilies([]);
        setSelectedTestspec('');
        setAnalysisResult({ candidates: [], summary: {}, context: [] });
        setError(null);
    };

    const handleProbabilityClick = (probability) => {
        setModalDetails(groupedProbabilities[probability]);
        setShowModal(true);
    };

    return (
        <div className={styles.container}>
            <GoToDashboardButton />

            <header className={styles.pageHeader}>
                <div>
                    <p className={styles.eyebrow}>{t('Root cause')}</p>
                    <h2 className={styles.title}>{t('Root Cause Analysis')}</h2>
                    <p className={styles.description}>
                        {t('Select a failing test step and get a ranked evidence report: enrichment against controls, timing, near-limit behavior, retries, and example units.')}
                    </p>
                </div>
                <button className={styles.helpButtonInline} onClick={() => setIsHelpOpen(true)}>{t('Page help')}</button>
            </header>

            {error && <div className={styles.errorMessage}>{error}</div>}

            <section className={styles.filterContainer}>
                <div className={styles.filterHeader}>
                    <div>
                        <h3>{t('Filters')}</h3>
                        <p>{t('Focus the analysis by primary step, date, software version, and serial number.')}</p>
                    </div>
                    <button onClick={resetFilters} className={styles.resetButton}>{t('Reset')}</button>
                </div>

                <div className={styles.filterGrid}>
                    <div className={styles.filterField}>
                        <label>{t('Primary step')}</label>
                        <select value={selectedStepNumber} onChange={(event) => setSelectedStepNumber(event.target.value)} className={styles.dropdown}>
                            <option value="">{t('All steps')}</option>
                            {stepNumbers.map((step) => (
                                <option key={step.stepNumber} value={step.stepNumber}>
                                    {t('Step')} {step.stepNumber} - {step.stepName || t('Unnamed step')} ({step.failSessions} {t('fails')})
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.filterField}>
                        <label>{t('Start date')}</label>
                        <DatePicker selected={startDate} onChange={setStartDate} placeholderText={t('Any start date')} className={styles.filterInput} isClearable />
                    </div>
                    <div className={styles.filterField}>
                        <label>{t('End date')}</label>
                        <DatePicker selected={endDate} onChange={setEndDate} placeholderText={t('Any end date')} className={styles.filterInput} isClearable minDate={startDate} />
                    </div>
                    <div className={styles.filterField}>
                        <label>{t('ATE SW version')}</label>
                        <select value={ateSwVersion} onChange={(event) => setAteSwVersion(event.target.value)} className={styles.dropdown}>
                            <option value="">{t('All versions')}</option>
                            {ateSwVersions.map((version) => (
                                <option key={version.value} value={version.value}>{version.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className={styles.filterField}>
                        <label>{t('Testspec')}</label>
                        <select value={selectedTestspec} onChange={(event) => setSelectedTestspec(event.target.value)} className={styles.dropdown}>
                            <option value="">{t('All testspecs')}</option>
                            {testspecs.map((testspec) => (
                                <option key={testspec.value} value={testspec.value}>{testspec.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className={`${styles.filterField} ${styles.serialFilter}`}>
                        <label>{t('Serial numbers')}</label>
                        <Select
                            isMulti
                            options={serialNumbers}
                            value={selectedSerialNumbers}
                            onChange={(values) => setSelectedSerialNumbers(values || [])}
                            placeholder={t('All serial numbers')}
                            className={styles.multiSelect}
                            classNamePrefix="rootCauseSelect"
                        />
                    </div>
                </div>

                <div className={styles.filterFooter}>
                    <span className={styles.filterHint}>{selectedStepNumber ? t('Primary step {{step}}', { step: selectedStepNumber }) : t('No primary step selected')}</span>
                    <button onClick={() => fetchAnalysisData()} className={styles.applyButton}>{loading ? t('Analyzing...') : t('Run analysis')}</button>
                </div>

                <SourceFileSummary
                    compact
                    startDate={startDate}
                    endDate={endDate}
                    ateSwVersion={ateSwVersion}
                    testspec={selectedTestspec}
                    serialNumbers={selectedSerialNumbers}
                    selectedFamilies={selectedFamilies}
                    onFamiliesChange={setSelectedFamilies}
                    title={t('Source files used for dashboard charts')}
                />
            </section>

            <section className={styles.metricsGrid}>
                {metrics.map((metric) => (
                    <div className={`${styles.metricCard} ${styles[metric.tone]}`} key={metric.label}>
                        <span>{metric.label}</span>
                        <strong>{metric.value}</strong>
                    </div>
                ))}
            </section>

            <section className={styles.analysisCard} id="chartjs-chart">
                <div className={styles.panelHeader}>
                    <div>
                        <h3>{t('Related-step probabilities')}</h3>
                        <p>{t('Scores compare target-failure sessions with target-nonfailure sessions. Higher means stronger root-cause evidence.')}</p>
                    </div>
                    <button onClick={() => setShowProbabilities((visible) => !visible)} className={styles.toggleButton}>
                        {showProbabilities ? t('Hide probabilities') : t('Show probabilities')}
                    </button>
                </div>

                <div className={styles.chartContainer}>
                    {loading ? (
                        <div className={styles.loadingContainer}><div className={styles.spinner}></div><p>{t('Running root cause analysis...')}</p></div>
                    ) : hasData ? (
                        <Bar data={chartData} options={rootCauseChartOptions} />
                    ) : (
                        <div className={styles.emptyState}>{t('Run the analysis to see related-step probabilities.')}</div>
                    )}
                </div>

                {summary.methodology && (
                    <div className={styles.methodologyBox}>
                        {summary.methodology}
                    </div>
                )}

                {context.length > 0 && (
                    <div className={styles.contextGrid}>
                        {context.map((item) => (
                            <span key={`${item.type}-${item.value}`} className={styles.contextChip}>
                                {item.type}: {item.value} ({item.count})
                            </span>
                        ))}
                    </div>
                )}

                {showProbabilities && hasData && (
                    <ul className={styles.probabilityList}>
                        {Object.entries(groupedProbabilities)
                            .sort(([a], [b]) => Number(b) - Number(a))
                            .map(([probability]) => (
                                <li
                                    key={probability}
                                    onClick={() => handleProbabilityClick(probability)}
                                    className={Number(probability) >= 70 ? styles.highlight : ''}
                                >
                                    {t('Evidence score bucket')}: {probability}
                                </li>
                            ))}
                    </ul>
                )}
            </section>

            {hasData && (
                <section className={styles.candidatePanel}>
                    <div className={styles.panelHeader}>
                        <div>
                            <h3>{t('Ranked candidates')}</h3>
                            <p>{t('Use the evidence lines to decide what to inspect first.')}</p>
                        </div>
                    </div>
                    <div className={styles.candidateList}>
                        {candidates.map((candidate, index) => (
                            <article className={styles.candidateCard} key={candidate.relatedStep}>
                                <div className={styles.candidateHeader}>
                                    <div>
                                        <span className={styles.rank}>#{index + 1}</span>
                                        <h4>Step {candidate.relatedStep}: {candidate.relatedStepName}</h4>
                                    </div>
                                    <strong>{candidate.rootCauseScore} score</strong>
                                </div>
                                <div className={styles.evidenceGrid}>
                                    <span>Case fail rate: {candidate.caseFailRate}%</span>
                                    <span>Control fail rate: {candidate.controlFailRate}%</span>
                                    <span>Odds ratio: {candidate.oddsRatio}</span>
                                    <span>Lift: {candidate.lift}</span>
                                    <span>Temporal support: {candidate.temporalSupport}%</span>
                                    <span>Near-limit sessions: {candidate.nearLimitSessions}</span>
                                    <span>Retry sessions: {candidate.retrySessions}</span>
                                </div>
                                <ul className={styles.evidenceList}>
                                    {(candidate.evidence || []).map((line) => <li key={line}>{line}</li>)}
                                </ul>
                                <p className={styles.recommendation}>{candidate.recommendation}</p>
                                {candidate.exampleSerials?.length > 0 && (
                                    <p className={styles.examples}>Example serials: {candidate.exampleSerials.join(', ')}</p>
                                )}
                            </article>
                        ))}
                    </div>
                </section>
            )}

            <section className={styles.cardFooter}>
                <button onClick={exportRootCauseChartAsPNG} className={styles.exportButton} disabled={!hasData}>{t('Chart PNG')}</button>
                <button onClick={() => exportRootCausePDF(groupedProbabilities)} className={styles.exportButton} disabled={!hasData}>PDF</button>
                <button onClick={() => exportRootCauseCSV(groupedProbabilities)} className={styles.exportButton} disabled={!hasData}>CSV</button>
            </section>

            {showModal && modalDetails && (
                <div className={styles.modal}>
                    <div className={styles.modalContent}>
                        <button className={styles.close} onClick={() => setShowModal(false)}>{t('Close')}</button>
                        <h3>Related steps in score bucket {Math.round(Number(modalDetails[0].rootCauseScore ?? modalDetails[0].probability) || 0)}</h3>
                        <ul>
                            {modalDetails.map((step) => (
                                <li key={step.relatedStep}>Step {step.relatedStep}: {step.rootCauseScore ?? step.probability} score</li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}

            <Modal isOpen={isHelpOpen} onRequestClose={() => setIsHelpOpen(false)} className={styles.helpModalContent} overlayClassName={styles.helpModalOverlay} contentLabel="Help Information">
                <button onClick={() => setIsHelpOpen(false)} className={styles.closeHelpButton}>{t('Close')}</button>
                <div className={styles.helpText}>
                    <h2>{t('About root cause analysis')}</h2>
                    <p>{t('This page analyzes uploaded failure data for a primary test step and ranks related steps by root-cause probability.')}</p>
                    <p>{t('Use filters to narrow the data by time, ATE software version, and serial numbers.')}</p>
                </div>
            </Modal>
        </div>
    );
};

export default RootCauseAnalysis;
